import { ArrowsLeftRight } from "@phosphor-icons/react"
import { useMemo, useState } from "react"

import CollectionsSelect from "~/components/collections/CollectionsSelect"
import { Button } from "~/components/reusable/Button"
import {
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogBodyV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import {
  useGetCollections,
  useMoveVariantsToCollection,
  type ICollectionSimple,
} from "~/hooks/useCollections"
import { getCollectionTypeLabel, type ICollectionType } from "~/models/collection"

type Props = {
  variantIds: string[]
  sourceCollectionId: number
  onSuccess?: () => void
}

export const MoveVariantsToCollectionDialog = ({
  variantIds,
  sourceCollectionId,
  onSuccess,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [targetCollection, setTargetCollection] = useState<ICollectionSimple | null>(null)

  const { mutate: moveVariants, isPending } = useMoveVariantsToCollection()
  const { data: collections } = useGetCollections()

  const sourceCollectionType = collections?.find((c) => c.id === sourceCollectionId)?.collectionType

  const excludeCollectionTypes = useMemo<ICollectionType[]>(() => {
    if (sourceCollectionType === "mixed") return ["noos"]
    return []
  }, [sourceCollectionType])

  const onSubmit = () => {
    if (!targetCollection) return
    moveVariants(
      {
        sourceCollectionId,
        targetCollectionId: targetCollection.id,
        variantIds,
      },
      {
        onSuccess: () => {
          setOpen(false)
          setTargetCollection(null)
          onSuccess?.()
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setTargetCollection(null)
      }}
    >
      <DialogTrigger requires="edit" asChild>
        <Button size="sm" variant="neutral">
          <ArrowsLeftRight />
          Move
        </Button>
      </DialogTrigger>
      <DialogContentV2>
        <DialogHeaderV2>
          <DialogTitle>Move variants to collection</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2 className="flex flex-col gap-3">
          <p className="text-sm">
            Select a destination collection. The selected variants will be removed from the current
            collection and added to the chosen one.
          </p>
          {sourceCollectionType && (
            <p className="text-fg-subtle text-xs">
              Only showing {getCollectionTypeLabel(sourceCollectionType)} collections to match the
              source collection type.
            </p>
          )}
          <CollectionsSelect
            selectedCollectionIds={targetCollection ? [targetCollection.id] : []}
            onCollectionSelectChange={(collections) => setTargetCollection(collections[0] ?? null)}
            excludedCollectionIds={[sourceCollectionId]}
            excludeCollectionTypes={excludeCollectionTypes}
            excludeExpired
            placeholder="Select destination collection..."
            autoFocus
          />
        </DialogBodyV2>
        <DialogFooterV2 className="justify-end gap-2">
          <DialogClose>
            <Button asChild variant="transparent">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            onClick={onSubmit}
            loading={isPending}
            disabled={!targetCollection}
          >
            {isPending ? "Moving variants..." : "Move variants"}
          </Button>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
